import { useEffect, useState } from 'react';

type Theme = 'light' | 'dark' | 'system';

function getInitialTheme(): Theme {
  const saved = typeof window !== 'undefined' ? (localStorage.getItem('theme') as Theme | null) : null;
  return saved && ['light', 'dark', 'system'].includes(saved) ? saved : 'system';
}

function resolveTheme(theme: Theme): 'light' | 'dark' {
  if (theme !== 'system') return theme;
  if (typeof window === 'undefined') return 'light';
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

export function useTheme() {
  const [theme, setThemeState] = useState<Theme>(getInitialTheme);
  const [resolved, setResolved] = useState<'light' | 'dark'>(() => resolveTheme(getInitialTheme()));

  useEffect(() => {
    const apply = (t: Theme) => {
      const r = resolveTheme(t);
      setResolved(r);
      if (r === 'dark') {
        document.documentElement.classList.add('dark');
      } else {
        document.documentElement.classList.remove('dark');
      }
      localStorage.setItem('theme', t);
    };

    apply(theme);

    const listener = () => {
      if (theme === 'system') {
        apply('system');
      }
    };
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    mq.addEventListener('change', listener);
    return () => mq.removeEventListener('change', listener);
  }, [theme]);

  const setTheme = (t: Theme) => setThemeState(t);
  const toggle = () => {
    const resolvedNow = resolveTheme(theme);
    setTheme(resolvedNow === 'dark' ? 'light' : 'dark');
  };

  return { theme, resolved, setTheme, toggle };
}
